import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Card, 
  CardContent, 
  IconButton, 
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import PageHeader from '../components/PageHeader';
import DashboardCard from '../components/DashboardCard';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { getSchoolSubscriptionStatus, SchoolSubscriptionStatus } from '../services/subscriptionService';
import axios from 'axios';


interface DashboardStats {
  totalStudents: number;
  totalTeachers: number;
  totalCourses: number;
  totalCameras: number;
  todayAttendance: number;
  totalAttendance: number;
  attendanceRate: number;
  totalInvoices: number;
  paidInvoices: number;
  outstandingInvoices: number;
  totalRevenue: number;
}

interface RecentActivity {
  id: string;
  type: 'attendance' | 'enrollment' | 'camera' | 'course' | 'invoice' | 'payment';
  message: string;
  timestamp: string;
  status: 'success' | 'warning' | 'info';
}



const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<SchoolSubscriptionStatus | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);





  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.school_id) return;
      
      try {
        setLoading(true);
        // Fetch all relevant data for the school
        const [studentsRes, teachersRes, coursesRes, camerasRes] = await Promise.all([
                      api.get('/students/', { params: { school_id: user.school_id, limit: 10000 } }),
          api.get('/users/', { params: { school_id: user.school_id, role: 'teacher' } }),
          api.get('/courses/', { params: { school_id: user.school_id } }),
                      api.get('/cameras/', { params: { school_id: user.school_id } })
        ]);

        const totalStudents = (studentsRes.data as any[]).length;
        const totalTeachers = (teachersRes.data as any[]).length;
        const totalCourses = (coursesRes.data as any[]).length;
        const totalCameras = (camerasRes.data as any[]).length;
        
        // Try to get today's attendance, but don't fail if it doesn't work
        let todayAttendance = 0;
        let totalAttendance = 0;
        let attendanceRate = 0;
        
        try {
          const attendanceRes = await api.get('/attendance/today', { params: { school_id: user.school_id } });
          todayAttendance = (attendanceRes.data as any).present || 0;
          totalAttendance = (attendanceRes.data as any).total || 0;
          attendanceRate = totalAttendance > 0 ? Math.round((todayAttendance / totalAttendance) * 100) : 0;
        } catch (attendanceErr) {
          console.warn('Failed to fetch attendance data:', attendanceErr);
          // Use fallback values
          todayAttendance = 0;
          totalAttendance = totalStudents;
          attendanceRate = 0;
        }

        // Fetch invoice data
        let totalInvoices = 0;
        let paidInvoices = 0;
        let outstandingInvoices = 0;
        let totalRevenue = 0;
        
        try {
          const invoicesRes = await api.get('/invoices', { params: { school_id: user.school_id } });
          const invoices = invoicesRes.data as any[];
          totalInvoices = invoices.length;
          paidInvoices = invoices.filter((inv: any) => inv.status === 'paid').length;
          outstandingInvoices = invoices.filter((inv: any) => inv.status === 'sent' || inv.status === 'overdue').length;
          totalRevenue = invoices
            .filter((inv: any) => inv.status === 'paid')
            .reduce((sum: number, inv: any) => sum + inv.total_amount, 0);
        } catch (invoiceErr) {
          console.warn('Failed to fetch invoice data:', invoiceErr);
        }

        setStats({
          totalStudents,
          totalTeachers,
          totalCourses,
          totalCameras,
          todayAttendance,
          totalAttendance,
          attendanceRate,
          totalInvoices,
          paidInvoices,
          outstandingInvoices,
          totalRevenue
        });

        // Fetch real recent activities from backend
        try {
          const activityRes = await api.get('/activity_logs/recent', { params: { school_id: user.school_id, limit: 10 } });
          setRecentActivities(activityRes.data as RecentActivity[]);
        } catch (activityErr) {
          console.warn('Failed to fetch activity logs:', activityErr);
          setRecentActivities([]);
        }

      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    const fetchSubscription = async () => {
      if (!user?.school_id) return;
      setSubscriptionLoading(true);
      try {
        const status = await getSchoolSubscriptionStatus(user.school_id);
        setSubscriptionStatus(status as SchoolSubscriptionStatus | null);
      } catch (err) {
        setSubscriptionStatus(null);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchDashboardData();
    fetchSubscription();
  }, [user]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'attendance': return <EventAvailableIcon />;
      case 'enrollment': return <PeopleIcon />;
      case 'camera': return <CameraAltIcon />;
      case 'course': return <MenuBookIcon />;
      case 'invoice': return <ReceiptIcon />;
      case 'payment': return <PaymentIcon />;
      default: return <TrendingUpIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader title="School Admin Dashboard" />
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="School Admin Dashboard" />
      


      {/* Subscription Warning Banner */}
      {!subscriptionLoading && (!subscriptionStatus || subscriptionStatus.subscription_status !== 'active') && (
        <Alert severity="warning" sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" variant="outlined" onClick={() => navigate('/school-subscription')}>
              Go to Subscription Page
            </Button>
          }
        >
          Your school does not have an active subscription. You must have an active subscription to use all features. Please contact your administrator or subscribe to a package to proceed.
        </Alert>
      )}
      
      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 3, 
        mb: 4 
      }}>
        <DashboardCard
          title="Total Students"
          value={stats?.totalStudents || 0}
          icon={<PeopleIcon />}
          color="#1E3A8A"
          onClick={() => navigate('/students')}
        />
        <DashboardCard
          title="Total Teachers"
          value={stats?.totalTeachers || 0}
          icon={<SchoolIcon />}
          color="#F97316"
          onClick={() => navigate('/users')}
        />
        <DashboardCard
          title="Active Courses"
          value={stats?.totalCourses || 0}
          icon={<MenuBookIcon />}
          color="#059669"
          onClick={() => navigate('/courses')}
        />
        <DashboardCard
          title="Today's Attendance"
          value={stats?.todayAttendance || 0}
          icon={<EventAvailableIcon />}
          color="#DC2626"
          onClick={() => navigate('/attendance')}
        />
      </Box>

      {/* Quick Actions and Recent Activity */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 3 
      }}>
        {/* Quick Actions */}
        <Paper sx={{ p: 3, height: 'fit-content' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1E3A8A', fontWeight: 600 }}>
            Quick Actions
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2 
          }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => navigate('/students/add')}
              sx={{ 
                borderColor: '#1E3A8A', 
                color: '#1E3A8A',
                '&:hover': { borderColor: '#1E3A8A', backgroundColor: '#1E3A8A10' }
              }}
            >
              Add Student
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => navigate('/courses/add')}
              sx={{ 
                borderColor: '#F97316', 
                color: '#F97316',
                '&:hover': { borderColor: '#F97316', backgroundColor: '#F9731610' }
              }}
            >
              Add Course
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => navigate('/cameras/add')}
              sx={{ 
                borderColor: '#059669', 
                color: '#059669',
                '&:hover': { borderColor: '#059669', backgroundColor: '#05966910' }
              }}
            >
              Add Camera
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => navigate('/users/add')}
              sx={{ 
                borderColor: '#DC2626', 
                color: '#DC2626',
                '&:hover': { borderColor: '#DC2626', backgroundColor: '#DC262610' }
              }}
            >
              Add Teacher
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ReceiptIcon />}
              onClick={() => navigate('/school-invoices')}
              sx={{ 
                borderColor: '#9C27B0', 
                color: '#9C27B0',
                '&:hover': { borderColor: '#9C27B0', backgroundColor: '#9C27B010' }
              }}
            >
              View Invoices
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PaymentIcon />}
              onClick={() => navigate('/school-subscription')}
              sx={{ 
                borderColor: '#FF5722', 
                color: '#FF5722',
                '&:hover': { borderColor: '#FF5722', backgroundColor: '#FF572210' }
              }}
            >
              Subscription
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#1E3A8A' }}>
            Attendance Overview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" sx={{ color: '#F97316', fontWeight: 700 }}>
              {stats?.attendanceRate || 0}%
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Today's Rate
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats?.todayAttendance || 0} of {stats?.totalAttendance || 0} students
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#1E3A8A' }}>
            Invoice Overview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" sx={{ color: '#9C27B0', fontWeight: 700 }}>
              {stats?.paidInvoices || 0}
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Paid Invoices
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats?.outstandingInvoices || 0} outstanding
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total Paid: KES {stats?.totalRevenue?.toLocaleString() || 0}
            </Typography>
          </Box>
        </Paper>

        {/* Recent Activity */}
        <Paper sx={{ p: 3, height: 'fit-content' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1E3A8A', fontWeight: 600 }}>
            Recent Activity
          </Typography>
          {recentActivities.length === 0 ? (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>No recent activity to display.</Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {recentActivities.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40, color: '#1E3A8A' }}>
                      {getActivityIcon(activity.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.message}
                      secondary={new Date(activity.timestamp).toLocaleString()}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                    <Chip
                      label={activity.status}
                      size="small"
                      color={getStatusColor(activity.status) as any}
                      sx={{ ml: 1 }}
                    />
                  </ListItem>
                  {index < recentActivities.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Box>

      {/* Management Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 3, 
        mt: 2 
      }}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #1E3A8A15 0%, #1E3A8A08 100%)',
          border: '1px solid #1E3A8A20',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(30, 58, 138, 0.15)',
            borderColor: '#1E3A8A40'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon sx={{ color: '#1E3A8A', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#1E3A8A', fontWeight: 600 }}>
                Students Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Manage student enrollments, profiles, and attendance records
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate('/students')}
                sx={{ 
                  backgroundColor: '#1E3A8A',
                  '&:hover': { backgroundColor: '#1E3A8A' }
                }}
              >
                View All
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/students/add')}
                sx={{ 
                  borderColor: '#1E3A8A', 
                  color: '#1E3A8A',
                  '&:hover': { borderColor: '#1E3A8A', backgroundColor: '#1E3A8A10' }
                }}
              >
                Add New
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #F9731615 0%, #F9731608 100%)',
          border: '1px solid #F9731620',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(249, 115, 22, 0.15)',
            borderColor: '#F9731640'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MenuBookIcon sx={{ color: '#F97316', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#F97316', fontWeight: 600 }}>
                Courses Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Create and manage courses, class sessions, and enrollments
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate('/courses')}
                sx={{ 
                  backgroundColor: '#F97316',
                  '&:hover': { backgroundColor: '#F97316' }
                }}
              >
                View All
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/courses/add')}
                sx={{ 
                  borderColor: '#F97316', 
                  color: '#F97316',
                  '&:hover': { borderColor: '#F97316', backgroundColor: '#F9731610' }
                }}
              >
                Add New
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #05966915 0%, #05966908 100%)',
          border: '1px solid #05966920',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(5, 150, 105, 0.15)',
            borderColor: '#05966940'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CameraAltIcon sx={{ color: '#059669', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#059669', fontWeight: 600 }}>
                Cameras Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Configure cameras, monitor connections, and manage locations
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate('/cameras')}
                sx={{ 
                  backgroundColor: '#059669',
                  '&:hover': { backgroundColor: '#059669' }
                }}
              >
                View All
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/cameras/add')}
                sx={{ 
                  borderColor: '#059669', 
                  color: '#059669',
                  '&:hover': { borderColor: '#059669', backgroundColor: '#05966910' }
                }}
              >
                Add New
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #9C27B015 0%, #9C27B008 100%)',
          border: '1px solid #9C27B020',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(156, 39, 176, 0.15)',
            borderColor: '#9C27B040'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ReceiptIcon sx={{ color: '#9C27B0', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#9C27B0', fontWeight: 600 }}>
                Invoice Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              View invoices, track payments, and manage subscription billing
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate('/school-invoices')}
                sx={{ 
                  backgroundColor: '#9C27B0',
                  '&:hover': { backgroundColor: '#9C27B0' }
                }}
              >
                View Invoices
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PaymentIcon />}
                onClick={() => navigate('/school-subscription')}
                sx={{ 
                  borderColor: '#9C27B0', 
                  color: '#9C27B0',
                  '&:hover': { borderColor: '#9C27B0', backgroundColor: '#9C27B010' }
                }}
              >
                Subscription
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AdminDashboard; 